/* Workspace — Courses (training library)
 *
 *  Endpoints:
 *    GET  /api/courses                                → [{ id, title, description, thumbnail, file_count, file_ids, cover_video_id }]
 *    GET  /api/courses/:id                            → { ..., files: [{id, filename, filetype, filesize, sort_order}] }
 *    GET  /api/courses/:id/thumbnail                  → image
 *    GET  /api/courses/:id/files/:fileId?download=0   → inline preview
 *    GET  /api/courses/:id/files/:fileId/content      → text content (for md/text)
 *    GET  /api/courses/:id/progress                   → { completed: [file_ids] }
 *    POST /api/courses/:id/progress                   { file_id } → toggle complete
 */

/* 允許的副檔名 — 鏡像後端 routes_courses.py 的 ALLOWED_EXTENSIONS，
 * 讓使用者在送出前就拿到回饋（後端對不合法副檔名是靜默略過）。 */
const COURSE_ALLOWED_EXTS = [
  '.ppt', '.pptx', '.mp4', '.webm', '.mov', '.avi',
  '.md', '.docx', '.doc', '.pdf',
  '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.xlsx', '.xls', '.csv', '.txt',
];

/* Expressive 外殼專屬：平板寬度改兩欄（≤767 單欄由 index.html 既有規則處理）。No-op under Bento. */
GxCore.injectCss('gx-css-p-courses', String.raw`
@media (min-width:768px) and (max-width:1099px){
html[data-ui-shell="expressive"] .hr-courses-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}
}
html[data-ui-shell="expressive"] .hr-courses-grid{gap:16px!important}
`);

function courseFmtSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function courseExtOf(name) {
  const i = (name || '').lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

/* 由標題產生穩定的色相 → 每門課漸層佔位圖各異，但維持低彩度耐看。 */
function courseHash(str) {
  let h = 5381;
  const s = str || '';
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

function courseGradient(title) {
  const hue = courseHash(title) % 360;
  const hue2 = (hue + 40) % 360;
  return `linear-gradient(135deg, hsl(${hue}, 30%, 40%), hsl(${hue2}, 36%, 26%))`;
}

/* 取標題第一個有意義的字元當佔位圖大字。 */
function courseInitial(title) {
  const t = (title || '').trim();
  return t ? t[0].toUpperCase() : '?';
}

function CoursesPage() {
  const toast = useToast();
  const [courses, setCourses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openCourse, setOpenCourse] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const admin = isAdmin(window._USER);

  const loadCourses = React.useCallback(() => {
    return api('GET', '/courses')
      .then(d => setCourses(Array.isArray(d) ? d : []))
      .catch(e => toast(e.detail || '課程載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  React.useEffect(() => { loadCourses(); }, [loadCourses]);

  return (
    <>
      <PageHeader
        eyebrow="工作空間 · Training"
        title="教學"
        italic="Courses"
        action={admin ? (
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>新增課程</Button>
        ) : null}
      />

      {creating && (
        <CourseCreateModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); loadCourses(); }}
        />
      )}

      {loading ? (
        <div className="hr-courses-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} height={220} />)}
        </div>
      ) : courses.length === 0 ? (
        <EmptyState
          icon="book"
          title="尚無課程"
          hint={admin ? '點右上「新增課程」上傳教材' : '請聯絡 admin 上傳教材'}
        />
      ) : (
        <div className="hr-courses-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {courses.map(c => (
            <CourseCard key={c.id} course={c} onClick={() => setOpenCourse(c)} />
          ))}
        </div>
      )}

      {openCourse && (
        <CourseViewer
          courseId={openCourse.id}
          admin={admin}
          onClose={() => setOpenCourse(null)}
          onChanged={loadCourses}
          onDeleted={() => { setOpenCourse(null); loadCourses(); }}
        />
      )}
    </>
  );
}

/* 共用：把 FileList 過濾成合法檔案 + 回報被略過的不合法副檔名。 */
function partitionCourseFiles(fileList) {
  const ok = [];
  const rejected = [];
  for (const f of Array.from(fileList || [])) {
    if (COURSE_ALLOWED_EXTS.includes(courseExtOf(f.name))) ok.push(f);
    else rejected.push(f.name);
  }
  return { ok, rejected };
}

/* 已選檔案清單 — 建課與追加共用。 */
function SelectedFileList({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
      {files.map((f, i) => (
        <div key={i} style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 10px', background: HR.surfaceAlt, borderRadius: 4,
          fontSize: FontSizes.trading_md,
        }, { padding: '8px 12px', background: GxT.color.s2, borderRadius: GxT.radius.btnSm, fontSize: 13 })}>
          <Icon name={fileIcon(courseExtOf(f.name))} size={12} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
          <span style={{ color: HR.inkFaint, fontFamily: HR.mono, flexShrink: 0 }}>{courseFmtSize(f.size)}</span>
          {onRemove && (
            <button onClick={() => onRemove(i)} style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: HR.inkFaint, padding: 0, fontSize: FontSizes.sm, lineHeight: 1, flexShrink: 0,
            }}>×</button>
          )}
        </div>
      ))}
    </div>
  );
}

function CourseCreateModal({ onClose, onCreated }) {
  const toast = useToast();
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [files, setFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const addFiles = (fileList) => {
    const { ok, rejected } = partitionCourseFiles(fileList);
    if (rejected.length) toast(`已略過不支援的檔案：${rejected.join('、')}`, 'error');
    if (ok.length) setFiles(prev => [...prev, ...ok]);
  };

  const submit = async () => {
    if (!title.trim()) { toast('請輸入課程標題', 'error'); return; }
    if (!files.length) { toast('請至少選擇一個檔案', 'error'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('description', description.trim());
      files.forEach(f => fd.append('files', f));
      const r = await fetch(`${API_BASE}/courses`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + getToken() },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw { detail: data.detail || `HTTP ${r.status}` };
      toast('課程建立成功', 'ok');
      onCreated();
    } catch (e) {
      toast(e.detail || '建立失敗', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open onClose={uploading ? () => {} : onClose}
      title="新增課程" subtitle="Create course" size="md"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} disabled={uploading}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={uploading}>
            {uploading ? '上傳中…' : '建立並上傳'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="課程標題" required>
          <TextInput value={title} onChange={setTitle} placeholder="例如：新人交易系統教學" />
        </Field>
        <Field label="課程描述" hint="選填">
          <Textarea value={description} onChange={setDescription} rows={3} placeholder="簡短說明這個課程的內容" />
        </Field>
        <Field label="教材檔案" required hint="支援影片(mp4/webm/mov/avi)、PDF、PPT、Word、Excel、圖片、Markdown/文字">
          <input
            ref={inputRef} type="file" multiple
            accept={COURSE_ALLOWED_EXTS.join(',')}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
          <Button variant="secondary" icon="plus" onClick={() => inputRef.current?.click()} disabled={uploading}>
            選擇檔案
          </Button>
          <SelectedFileList files={files} onRemove={(i) => setFiles(prev => prev.filter((_, idx) => idx !== i))} />
        </Field>
      </div>
    </Modal>
  );
}

function CourseCard({ course, onClick }) {
  // Expressive：卡片無框（透明框，hover 時仍以主色框線提示）
  const restBorder = uiPick(HR.rule, 'transparent');
  return (
    <div
      onClick={onClick}
      style={uiStyle({
        background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
        overflow: 'hidden', cursor: 'pointer',
        transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
        display: 'flex', flexDirection: 'column',
      }, { border: '1px solid transparent', borderRadius: GxT.radius.card })}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = HR.accent;
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,20,17,0.08)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = restBorder;
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{
        height: 140, background: HR.surfaceAlt,
        backgroundImage: course.thumbnail ? `url(/api/courses/${course.id}/thumbnail?token=${getToken()})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* 封面優先序：上傳縮圖 → 影片首幀 → 標題色漸層佔位 */}
        {!course.thumbnail && course.cover_video_id ? (
          <>
            <video
              src={`/api/courses/${course.id}/files/${course.cover_video_id}?download=0&token=${getToken()}#t=0.1`}
              preload="metadata" muted playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
            />
            <div style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              pointerEvents: 'none',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(14,20,17,0.55)', display: 'grid', placeItems: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}>
                {/* CSS 播放三角形（Icon 沒有 play 圖示）*/}
                <div style={{
                  width: 0, height: 0, marginLeft: 3,
                  borderTop: '8px solid transparent', borderBottom: '8px solid transparent',
                  borderLeft: '13px solid #fff',
                }} />
              </div>
            </div>
          </>
        ) : !course.thumbnail ? (
          <div style={{
            position: 'absolute', inset: 0, background: courseGradient(course.title),
            display: 'grid', placeItems: 'center', overflow: 'hidden',
          }}>
            <span style={uiStyle({
              fontFamily: HR.serif, fontSize: 56, fontWeight: 500, lineHeight: 1,
              color: 'rgba(255,255,255,0.92)', userSelect: 'none',
            }, { fontWeight: GxT.weight.display })}>{courseInitial(course.title)}</span>
          </div>
        ) : null}
        <div style={uiStyle({
          position: 'absolute', top: 10, right: 10,
          padding: '3px 8px', background: 'rgba(14,20,17,0.7)', color: '#fff',
          borderRadius: 3, fontSize: FontSizes.trading_md, fontFamily: HR.mono, letterSpacing: '0.06em',
        }, { top: 12, right: 12, padding: '4px 10px', background: 'rgba(14,20,17,.78)', borderRadius: GxT.radius.chip, fontFamily: GxT.font, fontWeight: 700, letterSpacing: 0 })}>{course.file_count || 0} 個檔案</div>
      </div>
      <div style={uiStyle({ padding: '14px 18px 18px' }, { padding: '16px 18px 18px' })}>
        <h3 style={uiStyle({
          fontFamily: HR.serif, fontSize: FontSizes.base, fontWeight: 500, margin: '0 0 6px',
          letterSpacing: '-0.015em', color: HR.ink,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }, { fontSize: GxT.size.h3, fontWeight: GxT.weight.title, letterSpacing: 0, lineHeight: 1.3 })}>{course.title}</h3>
        {course.description && (
          <p style={uiStyle({
            fontSize: FontSizes.xxs, color: HR.inkSoft, margin: '0 0 8px', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }, { fontSize: 13 })}>{course.description}</p>
        )}
        <div style={{
          fontSize: FontSizes.trading_md, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.04em',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>@{course.author}</span>
          <span>{fmtDate(course.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function fileIcon(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('video') || /mp4|mov|webm|avi/.test(t)) return 'activity';
  if (t.includes('pdf')) return 'file-text';
  if (t.includes('image') || /png|jpg|jpeg|gif|webp/.test(t)) return 'folder';
  if (t.includes('markdown') || /md|txt/.test(t)) return 'file-text';
  return 'file-text';
}

function CourseViewer({ courseId, admin, onClose, onChanged, onDeleted }) {
  const toast = useToast();
  const [course, setCourse] = React.useState(null);
  const [completed, setCompleted] = React.useState(new Set());
  const [activeFile, setActiveFile] = React.useState(null);
  const [textContent, setTextContent] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [managing, setManaging] = React.useState(false);
  const addRef = React.useRef(null);

  const loadCourse = React.useCallback((keepActive = false) => {
    return Promise.all([
      api('GET', `/courses/${courseId}`).catch(() => null),
      api('GET', `/courses/${courseId}/progress`).catch(() => ({ completed: [] })),
    ]).then(([c, p]) => {
      setCourse(c);
      setCompleted(new Set(p.completed || []));
      setActiveFile(prev => {
        if (!c || !c.files || c.files.length === 0) return null;
        if (keepActive && prev) {
          const still = c.files.find(f => f.id === prev.id);
          if (still) return still;
        }
        return c.files[0];
      });
    });
  }, [courseId]);

  React.useEffect(() => { loadCourse(false); }, [loadCourse]);

  const addFiles = async (fileList) => {
    const { ok, rejected } = partitionCourseFiles(fileList);
    if (rejected.length) toast(`已略過不支援的檔案：${rejected.join('、')}`, 'error');
    if (!ok.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      ok.forEach(f => fd.append('files', f));
      const r = await fetch(`${API_BASE}/courses/${courseId}/files`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + getToken() },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw { detail: data.detail || `HTTP ${r.status}` };
      toast(data.message || '已新增檔案', 'ok');
      await loadCourse(true);
      onChanged && onChanged();
    } catch (e) {
      toast(e.detail || '上傳失敗', 'error');
    } finally {
      setUploading(false);
    }
  };

  const deleteCourse = async () => {
    if (!confirm(`確定要刪除課程「${course?.title || ''}」？此操作會一併刪除所有教材，無法復原。`)) return;
    try {
      await api('DELETE', `/courses/${courseId}`);
      toast('課程已刪除', 'ok');
      onDeleted && onDeleted();
    } catch (e) {
      toast(e.detail || '刪除失敗', 'error');
    }
  };

  // Load text content when activeFile is text type
  React.useEffect(() => {
    if (!activeFile) return;
    const isText = /(markdown|text|md|txt)$/i.test(activeFile.filetype || activeFile.filename || '');
    if (isText) {
      api('GET', `/courses/${courseId}/files/${activeFile.id}/content`)
        .then(d => setTextContent(d.content))
        .catch(() => setTextContent(null));
    } else {
      setTextContent(null);
    }
  }, [activeFile, courseId]);

  const toggleProgress = async (fileId) => {
    try {
      const r = await api('POST', `/courses/${courseId}/progress`, { file_id: fileId });
      setCompleted(prev => {
        const next = new Set(prev);
        if (r.action === 'completed') next.add(fileId);
        else next.delete(fileId);
        return next;
      });
    } catch (e) { toast(e.detail || '失敗', 'error'); }
  };

  if (!course) {
    return (
      <Modal open onClose={onClose} title="—" subtitle="Loading" size="lg">
        <SkeletonText lines={6} />
      </Modal>
    );
  }

  const isMedia = (f) => /(video|mp4|mov|webm|avi)/i.test(f.filetype || f.filename);
  const isImage = (f) => /(image|png|jpg|jpeg|gif|webp)/i.test(f.filetype || f.filename);
  const isPdf = (f) => /pdf/i.test(f.filetype || f.filename);
  const isText = (f) => /(markdown|text|md|txt)$/i.test(f.filetype || f.filename);

  return (
    <>
    <Modal
      open onClose={onClose}
      title={course.title}
      subtitle={`${course.files.length} 檔案 · ${completed.size}/${course.files.length} 完成`}
      size="lg"
      footer={
        <>
          <span style={{ flex: 1, fontSize: FontSizes.xxs, color: HR.inkFaint }}>
            @{course.author} · {fmtDate(course.created_at)}
          </span>
          {admin && (
            <>
              <input
                ref={addRef} type="file" multiple
                accept={COURSE_ALLOWED_EXTS.join(',')}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                style={{ display: 'none' }}
              />
              <Button variant="secondary" icon="plus" onClick={() => addRef.current?.click()} disabled={uploading}>
                {uploading ? '上傳中…' : '新增檔案'}
              </Button>
              <Button variant="secondary" icon="edit" onClick={() => setManaging(true)} disabled={uploading}>編輯課程</Button>
              <Button variant="danger" onClick={deleteCourse} disabled={uploading}>刪除課程</Button>
            </>
          )}
          <Button variant="ghost" onClick={onClose}>關閉</Button>
        </>
      }
    >
      <div className="hr-courses-viewer" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16, minHeight: 420 }}>
        {/* File list */}
        <div style={{ borderRight: `1px solid ${HR.rule}`, paddingRight: 14, overflowY: 'auto', maxHeight: 520 }}>
          {course.description && (
            <div style={{
              fontSize: FontSizes.xxs, color: HR.inkSoft, lineHeight: 1.5, marginBottom: 12,
              paddingBottom: 12, borderBottom: `1px solid ${HR.ruleSoft}`,
            }}>
              {course.description}
            </div>
          )}
          {course.files.map(f => {
            const done = completed.has(f.id);
            const active = activeFile?.id === f.id;
            return (
              <div
                key={f.id}
                onClick={() => setActiveFile(f)}
                style={uiStyle({
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 4, marginBottom: 2,
                  cursor: 'pointer', fontSize: FontSizes.trading_md,
                  background: active ? HR.accentSoft : 'transparent',
                  color: active ? HR.accent : HR.ink,
                  border: active ? `1px solid ${GxCore.alpha(HR.accent, '33')}` : '1px solid transparent',
                }, {
                  padding: '10px 12px', minHeight: 40, borderRadius: GxT.radius.btnSm, fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  background: active ? GxT.color.sc : 'transparent', color: active ? GxT.color.onSc : GxT.color.on,
                  border: '1px solid transparent',
                })}
              >
                <button onClick={(e) => { e.stopPropagation(); toggleProgress(f.id); }} style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: done ? HR.accent : HR.inkFaint, padding: 0, fontSize: FontSizes.sm, lineHeight: 1,
                }}>{done ? '✓' : '○'}</button>
                <Icon name={fileIcon(f.filetype)} size={12} />
                <span style={{
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  textDecoration: done ? 'line-through' : 'none',
                  opacity: done ? 0.7 : 1,
                }}>{f.filename}</span>
              </div>
            );
          })}
        </div>

        {/* Preview */}
        <div style={{ minWidth: 0, overflow: 'auto', maxHeight: 520 }}>
          {!activeFile ? (
            <div style={{ padding: 40, textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs }}>
              選擇左邊的檔案開始閱讀
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                paddingBottom: 10, marginBottom: 12, borderBottom: `1px solid ${HR.ruleSoft}`,
              }}>
                <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.base, fontWeight: 500 },
                  { fontWeight: GxT.weight.title, minWidth: 0, overflowWrap: 'anywhere' })}>{activeFile.filename}</div>
                <a
                  href={`/api/courses/${courseId}/files/${activeFile.id}?download=1&token=${getToken()}`}
                  target="_blank" rel="noopener noreferrer"
                  style={uiStyle({
                    fontSize: FontSizes.trading_lg, color: HR.accent, textDecoration: 'none',
                    padding: '4px 10px', border: `1px solid ${GxCore.alpha(HR.accent, '33')}`, borderRadius: 3,
                  }, {
                    fontSize: 13, fontWeight: 700, color: GxT.color.onPc, background: GxT.color.pc,
                    padding: '8px 14px', border: 'none', borderRadius: GxT.radius.btnSm, flexShrink: 0, marginLeft: 12,
                  })}
                >下載</a>
              </div>
              {isMedia(activeFile) ? (
                <video controls style={{ width: '100%', maxHeight: 460, background: '#000', borderRadius: uiPick(4, GxT.radius.inner) }}
                  src={`/api/courses/${courseId}/files/${activeFile.id}?download=0&token=${getToken()}`} />
              ) : isImage(activeFile) ? (
                <img src={`/api/courses/${courseId}/files/${activeFile.id}?download=0&token=${getToken()}`}
                  style={{ maxWidth: '100%', borderRadius: uiPick(4, GxT.radius.inner) }} alt={activeFile.filename} />
              ) : isPdf(activeFile) ? (
                <iframe src={`/api/courses/${courseId}/files/${activeFile.id}?download=0&token=${getToken()}`}
                  style={uiStyle({ width: '100%', height: 460, border: `1px solid ${HR.rule}`, borderRadius: 4 },
                    { border: `1px solid ${GxT.color.olv}`, borderRadius: GxT.radius.inner })} />
              ) : isText(activeFile) ? (
                textContent === null ? (
                  <SkeletonText lines={8} />
                ) : (
                  <pre style={uiStyle({
                    margin: 0, padding: 14, background: HR.surfaceAlt, borderRadius: 4,
                    fontFamily: HR.serif, fontSize: FontSizes.trading_md, lineHeight: 1.7,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    maxHeight: 460, overflowY: 'auto',
                    color: HR.ink,
                  }, { padding: '16px 18px', background: GxT.color.s1, borderRadius: GxT.radius.inner, fontSize: 14 })}>{textContent}</pre>
                )
              ) : (
                <div style={uiStyle({
                  padding: 30, textAlign: 'center', color: HR.inkFaint,
                  background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.xs,
                }, { color: GxT.color.onV, background: GxT.color.s2, borderRadius: GxT.radius.inner, fontSize: 14 })}>
                  此檔案類型無法線上預覽，請按右上「下載」開啟
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
    {managing && (
      <CourseManageModal
        courseId={courseId}
        onClose={() => setManaging(false)}
        onChanged={() => { loadCourse(true); onChanged && onChanged(); }}
      />
    )}
    </>
  );
}

function CourseManageModal({ courseId, onClose, onChanged }) {
  const toast = useToast();
  const [course, setCourse] = React.useState(null);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [savingMeta, setSavingMeta] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    return api('GET', `/courses/${courseId}`)
      .then(c => {
        setCourse(c);
        setTitle(c.title || '');
        setDescription(c.description || '');
      })
      .catch(e => toast(e.detail || '載入失敗', 'error'));
  }, [courseId, toast]);

  React.useEffect(() => { load(); }, [load]);

  const saveMeta = async () => {
    if (!title.trim()) { toast('標題不可空白', 'error'); return; }
    setSavingMeta(true);
    try {
      await api('PATCH', `/courses/${courseId}`, { title: title.trim(), description: description.trim() });
      toast('課程已更新', 'ok');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast(e.detail || '更新失敗', 'error');
    } finally {
      setSavingMeta(false);
    }
  };

  const move = async (idx, dir) => {
    const files = course.files;
    const j = idx + dir;
    if (j < 0 || j >= files.length) return;
    const ids = files.map(f => f.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setBusy(true);
    try {
      await api('PUT', `/courses/${courseId}/files/order`, { file_ids: ids });
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast(e.detail || '排序失敗', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (f) => {
    if (!confirm(`刪除檔案「${f.filename}」？此操作無法復原。`)) return;
    setBusy(true);
    try {
      await api('DELETE', `/courses/${courseId}/files/${f.id}`);
      toast('檔案已刪除', 'ok');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast(e.detail || '刪除失敗', 'error');
    } finally {
      setBusy(false);
    }
  };

  const setCover = async (fileId) => {
    setBusy(true);
    try {
      await api('PUT', `/courses/${courseId}/cover`, { file_id: fileId });
      toast(fileId ? '封面已更新' : '已恢復自動封面', 'ok');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast(e.detail || '設定失敗', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!course) {
    return (
      <Modal open onClose={onClose} title="編輯課程" subtitle="Manage course" size="lg">
        <SkeletonText lines={6} />
      </Modal>
    );
  }

  const arrowBtn = (disabled, onClick, glyph) => (
    <button onClick={onClick} disabled={disabled} style={{
      background: 'transparent', border: 'none', cursor: disabled ? 'default' : 'pointer',
      color: disabled ? HR.inkFaint : HR.inkSoft, padding: 0, lineHeight: 0.8,
      fontSize: 10, opacity: disabled ? 0.4 : 1,
    }}>{glyph}</button>
  );

  return (
    <Modal
      open onClose={onClose}
      title="編輯課程" subtitle="Manage course" size="lg"
      footer={<><span style={{ flex: 1 }} /><Button variant="ghost" onClick={onClose}>關閉</Button></>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label="課程標題" required>
          <TextInput value={title} onChange={setTitle} placeholder="課程標題" />
        </Field>
        <Field label="課程描述" hint="選填">
          <Textarea value={description} onChange={setDescription} rows={3} placeholder="簡短說明這個課程的內容" />
        </Field>
        <div>
          <Button variant="primary" onClick={saveMeta} disabled={savingMeta}>
            {savingMeta ? '儲存中…' : '儲存標題／描述'}
          </Button>
        </div>

        <div style={{ borderTop: `1px solid ${HR.rule}`, paddingTop: 16 }}>
          <div style={uiStyle({
            fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: HR.inkFaint, fontWeight: 500, marginBottom: 4,
          }, { fontSize: 15, letterSpacing: 0, textTransform: 'none', color: GxT.color.on, fontWeight: GxT.weight.title })}>檔案管理（{course.files.length}）</div>
          <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginBottom: 10 }}>
            用 ▲▼ 調整顯示順序；圖片可設為課程封面；刪除無法復原。
          </div>
          {course.files.length === 0 ? (
            <div style={{ fontSize: FontSizes.xs, color: HR.inkFaint, padding: '12px 0' }}>尚無檔案</div>
          ) : (
            course.files.map((f, i) => {
              const isCover = course.thumbnail_file_id === f.id;
              const isImg = f.filetype === 'image';
              return (
                <div key={f.id} style={uiStyle({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', background: HR.surfaceAlt, borderRadius: 4, marginBottom: 4,
                }, { padding: '8px 12px', background: GxT.color.s2, borderRadius: GxT.radius.inner, marginBottom: 6 })}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {arrowBtn(busy || i === 0, () => move(i, -1), '▲')}
                    {arrowBtn(busy || i === course.files.length - 1, () => move(i, 1), '▼')}
                  </div>
                  <Icon name={fileIcon(f.filetype)} size={13} />
                  <span style={{
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontSize: FontSizes.trading_md, color: HR.ink,
                  }}>{f.filename}</span>
                  {isCover && <Pill tone="accent">封面</Pill>}
                  <span style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, fontFamily: HR.mono, flexShrink: 0 }}>
                    {courseFmtSize(f.filesize)}
                  </span>
                  {isImg && (
                    <Button size="sm" variant={isCover ? 'secondary' : 'ghost'} disabled={busy}
                      onClick={() => setCover(isCover ? null : f.id)}>
                      {isCover ? '取消封面' : '設為封面'}
                    </Button>
                  )}
                  <button onClick={() => removeFile(f)} disabled={busy} title="刪除檔案" style={{
                    background: 'transparent', border: 'none', cursor: busy ? 'default' : 'pointer',
                    color: HR.down, padding: 4, display: 'flex', opacity: busy ? 0.5 : 1,
                  }}>
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

window.CoursesPage = CoursesPage;
